import { Wrench, ShieldCheck, Clock, Handshake } from "lucide-react";
import { useContent } from "@/context/ContentContext";

const VALUES = [
  { icon: Wrench, title: "Craftsmanship", text: "Years of perfecting the trade — every weld, cut, and post." },
  { icon: Handshake, title: "Honest Bids", text: "Candid pricing. What we quote is what you pay." },
  { icon: Clock, title: "On Time", text: "When Hondo says the crew's showing up — the crew's showing up." },
  { icon: ShieldCheck, title: "Built to Last", text: "Materials and workmanship that outlast the weather." },
];

export default function About() {
  const { content } = useContent();
  const about = content?.about || {};
  const contact = content?.contact || {};
  const area = contact.area || "Central Texas";
  const photo = about.photo_data || about.photo_url || "";

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white border-y border-[#E5E7EB]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="font-display tracking-[0.3em] text-xs text-[#4AB300] mb-4">{about.eyebrow || "02 — THE MAN BEHIND THE BUILD"}</div>
          <h2 data-testid="about-heading" className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-[0.95] tracking-tight">
            Meet<br />
            <span className="text-[#4AB300]">{(about.owner_name || "Hondo Castruita").split(" ")[0]}</span><br />
            {(about.owner_name || "Hondo Castruita").split(" ").slice(1).join(" ")}
          </h2>

          {photo ? (
            <div className="mt-8 relative overflow-hidden border-2 border-[#84FB00] max-w-md" data-testid="about-photo">
              <img
                src={photo}
                alt={about.owner_name || "Hondo Castruita"}
                className="w-full aspect-[3/4] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white/95 via-white/60 to-transparent p-5">
                <p className="font-display uppercase text-sm tracking-widest text-[#4AB300]">
                  {about.owner_title || "Owner · Lead Welder"}
                </p>
                <p className="font-display uppercase text-sm tracking-widest text-[#374151] mt-1">{area}</p>
              </div>
            </div>
          ) : (
            <div className="mt-8 border-l-2 border-[#84FB00] pl-6">
              <p className="font-display uppercase text-lg tracking-widest text-[#4B5563]">{about.owner_title || "Owner · Lead Welder"}</p>
              <p className="font-display uppercase text-lg tracking-widest text-[#4B5563]">{area}</p>
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <div data-testid="about-text" className="space-y-6 text-lg md:text-xl text-[#1F2937] leading-relaxed">
            {(about.intro || "")
              .split(/\n\s*\n/)
              .map((part) => part.trim())
              .filter(Boolean)
              .map((para, pIdx) => (
                <p key={pIdx} data-testid={`about-paragraph-${pIdx}`}>
                  {about.years && para.includes(`${about.years} years`)
                    ? para.split(`${about.years} years`).flatMap((segment, i, arr) =>
                        i < arr.length - 1
                          ? [
                              segment,
                              <span key={i} className="text-[#4AB300] font-semibold">
                                {about.years} years
                              </span>,
                            ]
                          : [segment]
                      )
                    : para}
                </p>
              ))}
          </div>
          {about.quote && (
            <p className="mt-6 text-[#374151] leading-relaxed">
              "{about.quote}" — <span className="text-[#0A0E1A] font-medium">{about.quote_author}</span>
            </p>
          )}

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#E5E7EB]">
            {VALUES.map((v, i) => (
              <div key={i} className="bg-white p-6" data-testid={`value-${i}`}>
                <v.icon size={28} className="text-[#4AB300]" />
                <h4 className="font-display text-xl uppercase tracking-wide mt-4">{v.title}</h4>
                <p className="text-sm text-[#4B5563] mt-2 leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
